import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, DollarSign, Calendar, Building2,
  Download, BarChart3, PieChart
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const ReportsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // today, week, month, all

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, facilitiesRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/facilities`, { withCredentials: true })
      ]);
      setBookings(bookingsRes.data);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Veriler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  // Filter bookings by period
  const getFilteredBookings = () => {
    const now = new Date();
    
    return bookings.filter(b => {
      const bookingDate = new Date(b.date);
      
      switch (period) {
        case 'today':
          return bookingDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = subDays(now, 7);
          return bookingDate >= weekAgo;
        case 'month':
          const monthStart = startOfMonth(now);
          const monthEnd = endOfMonth(now);
          return bookingDate >= monthStart && bookingDate <= monthEnd;
        case 'all':
        default:
          return true;
      }
    });
  };

  const filteredBookings = getFilteredBookings();
  const confirmedBookings = filteredBookings.filter(b => b.payment_status === 'CONFIRMED');
  
  // Calculate stats
  const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (b.brut_tutar || 0), 0);
  const totalCommission = confirmedBookings.reduce((sum, b) => sum + (b.komisyon_tutar || 0), 0);
  const totalOperator = confirmedBookings.reduce((sum, b) => sum + (b.isletmeci_hakki || 0), 0);

  // Top facilities by bookings
  const facilityStats = {};
  confirmedBookings.forEach(b => {
    if (!facilityStats[b.facility_id]) {
      facilityStats[b.facility_id] = {
        facility_name: b.facility_name,
        count: 0,
        revenue: 0
      };
    }
    facilityStats[b.facility_id].count++;
    facilityStats[b.facility_id].revenue += b.brut_tutar || 0;
  });

  const topFacilities = Object.values(facilityStats)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Daily revenue chart data
  const dailyRevenue = {};
  confirmedBookings.forEach(b => {
    const date = b.date;
    if (!dailyRevenue[date]) {
      dailyRevenue[date] = 0;
    }
    dailyRevenue[date] += b.brut_tutar || 0;
  });

  const chartData = Object.entries(dailyRevenue)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-7); // Last 7 days

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Raporlar</h1>
          <p className="text-sm text-slate-600 mt-1">
            Detaylı istatistikler ve analizler
          </p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Bugün</SelectItem>
            <SelectItem value="week">Son 7 Gün</SelectItem>
            <SelectItem value="month">Bu Ay</SelectItem>
            <SelectItem value="all">Tüm Zamanlar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Toplam Gelir</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {totalRevenue.toLocaleString('tr-TR')} ₺
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Komisyon</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {totalCommission.toLocaleString('tr-TR')} ₺
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">İşletmeci Hakkı</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {totalOperator.toLocaleString('tr-TR')} ₺
                </p>
              </div>
              <Building2 className="w-10 h-10 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Onaylı Rezervasyon</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {confirmedBookings.length}
                </p>
              </div>
              <Calendar className="w-10 h-10 text-amber-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Revenue Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Günlük Gelir Trendi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {chartData.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Veri yok</p>
              ) : (
                chartData.map(([date, revenue]) => {
                  const maxRevenue = Math.max(...chartData.map(d => d[1]));
                  const percentage = (revenue / maxRevenue) * 100;
                  
                  return (
                    <div key={date} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{format(new Date(date), 'd MMM', { locale: tr })}</span>
                        <span className="font-semibold">{revenue.toLocaleString('tr-TR')} ₺</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-emerald-600 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Facilities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              En Çok Rezervasyon Alan Tesisler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topFacilities.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Veri yok</p>
              ) : (
                topFacilities.map((facility, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{facility.facility_name}</p>
                        <p className="text-xs text-slate-600">{facility.count} rezervasyon</p>
                      </div>
                    </div>
                    <p className="font-semibold text-emerald-700">
                      {facility.revenue.toLocaleString('tr-TR')} ₺
                    </p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportsPage;
